import React, {useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import EditorLayout from '../../../../components/Layouts/EditorLayout';
import {SMCSelect, SMCOption} from '../../../../components/Simple/SMCSelect';
import {PortalSettingsGetSchema} from '../../../../api/DBSchema/PortalSettings';
import {SMCCheckbox} from '../../../../components/Simple/SMCCheckbox';
import Indentation from '../../../../components/Simple/Indentation';
import SMCLabel from "../../../../components/Simple/SMCLabel";
import EmptyRow from "../../../../components/Simple/EmptyRow";
import SMCTextArea from "../../../../components/Simple/SMCTextArea";

const pageSchema = PortalSettingsGetSchema.pages.domainCyberReports;
const pageId = pageSchema.pageId;

function PortalSettingsCyberReports({
    portalSettingsObject,
    setPortalSettingsData
}) {
    const { t } = useTranslation();

    const pageData = portalSettingsObject[pageId];
    const setPageValue = useCallback((paramName, value) => {
        setPortalSettingsData([pageId, paramName], value);
    }, [setPortalSettingsData]);

    const domainCyberReportsActivation          = pageData[pageSchema.domainCyberReportsActivation];
    const domainCyberReportsDaily               = pageData[pageSchema.domainCyberReportsDaily];
    const domainCyberReportsDailyTime           = pageData[pageSchema.domainCyberReportsDailyTime];
    const domainCyberReportsWeekly              = pageData[pageSchema.domainCyberReportsWeekly];
    const domainCyberReportsWeekday             = pageData[pageSchema.domainCyberReportsWeekday];
    const domainCyberReportsWeekdayTime         = pageData[pageSchema.domainCyberReportsWeekdayTime];
    const domainCyberReportsMonthly             = pageData[pageSchema.domainCyberReportsMonthly];
    const domainCyberReportsMonthDay            = pageData[pageSchema.domainCyberReportsMonthDay];
    const domainCyberReportsMonthTime           = pageData[pageSchema.domainCyberReportsMonthTime];
    const domainCyberReportsContactRecipient    = pageData[pageSchema.domainCyberReportsContactRecipient];
    const domainCyberReportsAdditionalEmails    = pageData[pageSchema.domainCyberReportsAdditionalEmails];
    const domainCyberReportsAdditionalEmailsStr    = pageData[pageSchema.domainCyberReportsAdditionalEmailsStr];

    // Options of 00:00-01:00 until 23:00-00:00
    const hourTimeOptions = useMemo(() => (
        [...Array(24)].map((e,i) => (
            <SMCOption key={i} value={i}>{i<10?'0':''}{i}:00-{((i+1)%24)<10?'0':''}{(i+1)%24}:00</SMCOption>
        ))
    ), []);

    const dayOfWeekOptions = useMemo(() => (
        <>
            <SMCOption value={1}>{t('SMP.SUNDAY')}</SMCOption>
            <SMCOption value={2}>{t('SMP.MONDAY')}</SMCOption>
            <SMCOption value={3}>{t('SMP.TUESDAY')}</SMCOption>
            <SMCOption value={4}>{t('SMP.WEDNESDAY')}</SMCOption>
            <SMCOption value={5}>{t('SMP.THURSDAY')}</SMCOption>
            <SMCOption value={6}>{t('SMP.FRIDAY')}</SMCOption>
            <SMCOption value={7}>{t('SMP.SATURDAY')}</SMCOption>
        </>
    ), [t]);

    const dayOfMonthOptions = useMemo(() => (
        [...Array(31)].map((e,i) => (
            <SMCOption key={i+1} value={i+1}>{i+1}</SMCOption>
        ))
    ), [])

    const cyberReportsDisabled = !domainCyberReportsActivation;

    return (
        <EditorLayout>
            <SMCLabel
                bold
                style={{ fontSize: '15px' }}
            >
                {t('SMP.SETTINGS.DOMAIN_CYBER_REPORTS.NAME')}
            </SMCLabel>
            <SMCCheckbox
                checked={domainCyberReportsActivation}
                onChange={e => setPageValue(pageSchema.domainCyberReportsActivation, e.target.checked)}
                label={t('SMP.SETTINGS.DOMAIN_CYBER_REPORTS.ACTIVATION')}
            />
            <EmptyRow />
            <SMCLabel
                bold
                style={{ fontSize: '15px' }}
            >
                {t('SMP.PLANS.INNER_MENU.REPORT_RECIPIENTS')}
            </SMCLabel>
            <SMCCheckbox
                checked={domainCyberReportsContactRecipient}
                onChange={e => setPageValue(pageSchema.domainCyberReportsContactRecipient, e.target.checked)}
                label={"Send to Service Domain's contact"}
                disabled={cyberReportsDisabled}
            />
            <SMCCheckbox
                checked={domainCyberReportsAdditionalEmails}
                onChange={e => setPageValue(pageSchema.domainCyberReportsAdditionalEmails, e.target.checked)}
                label={t('SMP.PLAN.SERVICES.REPORT_RCIPIENTS.ADDITIONAL_EMAILS')}
                tooltip={t('SMP.PLAN.SERVICES.REPORT_RCIPIENTS.ADDITIONAL_EMAILS.TOOLTIP')}
                disabled={cyberReportsDisabled}
                inline
            />
            <Indentation>
                <SMCTextArea
                    value={domainCyberReportsAdditionalEmailsStr}
                    onChange={e => setPageValue(pageSchema.domainCyberReportsAdditionalEmailsStr, e.target.value)}
                    disabled={cyberReportsDisabled || !domainCyberReportsAdditionalEmails}
                    inline
                />
            </Indentation>
            <EmptyRow />
            <SMCLabel
                bold
                style={{ fontSize: '15px' }}
            >
                {t('SMC_SETTINGS_REPORT_SCHD')}
            </SMCLabel>
            <Indentation>
                <SMCCheckbox
                    checked={domainCyberReportsDaily}
                    onChange={e => setPageValue(pageSchema.domainCyberReportsDaily, e.target.checked)}
                    label={t('SMP.PLAN.SERVICES.REPORTS.SEND_DAILY')}
                    disabled={cyberReportsDisabled}
                />
                <Indentation>
                    <SMCSelect disabled={!domainCyberReportsDaily || cyberReportsDisabled} label={t('SMP.PLAN.SERVICES.FIRMWARE.TIME_OF_DAY')} value={domainCyberReportsDailyTime} onChange={e => setPageValue(pageSchema.domainCyberReportsDailyTime, e.target.value)}>
                        { hourTimeOptions }
                    </SMCSelect>
                </Indentation>
                <SMCCheckbox
                    checked={domainCyberReportsWeekly}
                    onChange={e => setPageValue(pageSchema.domainCyberReportsWeekly, e.target.checked)}
                    label={t('SMP.PLAN.SERVICES.REPORTS.SEND_WEEKLY')}
                    disabled={cyberReportsDisabled}
                />
                <Indentation>
                    <SMCSelect disabled={!domainCyberReportsWeekly || cyberReportsDisabled} label={t('SMP.PLAN.SERVICES.FIRMWARE.DAY_OF_WEEK')} value={domainCyberReportsWeekday} onChange={e => setPageValue(pageSchema.domainCyberReportsWeekday, parseInt(e.target.value))}>
                        { dayOfWeekOptions }
                    </SMCSelect>
                </Indentation>
                <Indentation>
                    <SMCSelect disabled={!domainCyberReportsWeekly || cyberReportsDisabled} label={t('SMP.PLAN.SERVICES.FIRMWARE.TIME_OF_DAY')} value={domainCyberReportsWeekdayTime} onChange={e => setPageValue(pageSchema.domainCyberReportsWeekdayTime, parseInt(e.target.value))}>
                        { hourTimeOptions }
                    </SMCSelect>
                </Indentation>
                <SMCCheckbox
                    checked={domainCyberReportsMonthly}
                    onChange={e => setPageValue(pageSchema.domainCyberReportsMonthly, e.target.checked)}
                    label={t('SMP.PLAN.SERVICES.REPORTS.SEND_MONTHLY')}
                    disabled={cyberReportsDisabled}
                />
                <Indentation>
                    <SMCSelect disabled={!domainCyberReportsMonthly || cyberReportsDisabled} label={t('SMP.PLAN.SERVICES.FIRMWARE.DAY_OF_MONTH')} value={domainCyberReportsMonthDay} onChange={e => setPageValue(pageSchema.domainCyberReportsMonthDay, parseInt(e.target.value))}>
                        { dayOfMonthOptions }
                    </SMCSelect>
                </Indentation>
                <Indentation>
                    <SMCSelect disabled={!domainCyberReportsMonthly || cyberReportsDisabled} label={t('SMP.PLAN.SERVICES.FIRMWARE.TIME_OF_DAY')} value={domainCyberReportsMonthTime} onChange={e => setPageValue(pageSchema.domainCyberReportsMonthTime, parseInt(e.target.value))}>
                        { hourTimeOptions }
                    </SMCSelect>
                </Indentation>
            </Indentation>
        </EditorLayout>
    )
}

export default PortalSettingsCyberReports;